import { useState, useEffect } from 'react';
// 1. 引入 Select 组件
import { Button, Dialog, MessagePlugin, Select } from 'tdesign-react';
import { Save } from 'lucide-react';
import type { ProductVO } from '@/types/api-entity';
import ProductRichTextEditor from './ProductRichTextEditor';
import ProductImageUploader from './ProductImageUploader';

interface ProductFormModalProps {
  visible: boolean;
  editingProduct: ProductVO | null;
  initialForm: Partial<ProductVO>;
  onSave: (form: Partial<ProductVO>) => Promise<void>;
  onCancel: () => void;
  error: string;
  categories: string[];
}

export default function ProductFormModal({
  visible,
  editingProduct,
  initialForm,
  onSave,
  onCancel,
  error,
  categories
}: ProductFormModalProps) {
  const [form, setForm] = useState<Partial<ProductVO>>(initialForm);

  useEffect(() => {
    setForm(initialForm);
  }, [initialForm, visible]);

  const handleSubmit = async () => {
    if (!form.productName?.trim()) {
      MessagePlugin.error('商品名称不能为空');
      return;
    }
    if (!form.price || form.price <= 0) {
      MessagePlugin.error('价格必须大于0');
      return;
    }
    if (form.stock === undefined || form.stock < 0) {
      MessagePlugin.error('库存不能为负数');
      return;
    }
    await onSave(form);
  };

  // 2. 将 categories字符串数组转换为 Select 需要的 options 格式
  const categoryOptions = categories.map((cat) => ({
    label: cat,
    value: cat,
  }));

  return (
    <Dialog
      header={editingProduct ? '编辑商品' : '新增商品'}
      visible={visible}
      onClose={onCancel}
      footer={false}
      width="800px"
    >
      <div className="space-y-4 p-2">
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-red-600 text-sm">
            {error}
          </div>
        )}
        
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            商品名称 <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={form.productName || ''}
            onChange={(e) => setForm({ ...form, productName: e.target.value })}
            className="w-full h-10 px-3 rounded-lg border border-gray-200 focus:border-[#1c5bff] outline-none"
            placeholder="请输入商品名称"
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">商品分类</label>
            {/* 3. 替换 input 为 Select 组件 */}
            <Select
              value={form.category}
              onChange={(value) => setForm({ ...form, category: value as string })}
              options={categoryOptions}
              placeholder="请选择商品分类"
              clearable
              className="w-full"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">商品编码</label>
            <input
              type="text"
              value={form.productCode || ''}
              onChange={(e) => setForm({ ...form, productCode: e.target.value })}
              className="w-full h-10 px-3 rounded-lg border border-gray-200 focus:border-[#1c5bff] outline-none"
              placeholder="商品编码"
            />
          </div>
        </div>

        <div className="grid grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              价格 <span className="text-red-500">*</span>
            </label>
            <input
              type="number"
              value={form.price || ''}
              onChange={(e) => setForm({ ...form, price: parseFloat(e.target.value) || 0 })}
              className="w-full h-10 px-3 rounded-lg border border-gray-200 focus:border-[#1c5bff] outline-none"
              placeholder="价格"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              库存 <span className="text-red-500">*</span>
            </label>
            <input
              type="number"
              value={form.stock === undefined ? '' : form.stock}
              onChange={(e) => setForm({ ...form, stock: parseInt(e.target.value) || 0 })}
              className="w-full h-10 px-3 rounded-lg border border-gray-200 focus:border-[#1c5bff] outline-none"
              placeholder="库存"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">单位</label>
            <input
              type="text"
              value={form.unit || ''}
              onChange={(e) => setForm({ ...form, unit: e.target.value })}
              className="w-full h-10 px-3 rounded-lg border border-gray-200 focus:border-[#1c5bff] outline-none"
              placeholder="吨"
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">主图</label>
          <ProductImageUploader
            value={form.mainImage}
            onChange={(url) => setForm({ ...form, mainImage: url })}
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">商品描述</label>
          <ProductRichTextEditor
            value={form.description}
            onChange={(val) => setForm({ ...form, description: val })}
          />
        </div>

        <div className="flex items-center justify-end gap-3 pt-4 border-t">
          <Button variant="outline" onClick={onCancel}>取消</Button>
          <Button theme="primary" icon={<Save className="w-4 h-4" />} onClick={handleSubmit}>
            保存
          </Button>
        </div>
      </div>
    </Dialog>
  );
}